import { AssignmentStatusEnum, CaseTypeEnum } from 'api/model';

const Roles = {
  Admin: 1,
  User: 2,
  GoSupport: 3,
  TrafikLedarAdmin: 4,
  GruppLedare: 5,
  SuperAdmin: 6,
  AnnonsAdmin: 7,
  Annons: 8,
  Approval: 9,
  Riddermark: 10,
  Ekonomi: 11,
  CircleK: 12,
  KvalitetOUtbildning: 13,
  ExternalUser: 14,
  ExternalAdmin: 15,
  HR: 16,
  Efterkontroll: 17,
  EfterkontrollAdmin: 18,
  RiddermarkLedning: 19,
  TeamLead: 20,
  TillgänglighetsVisare: 21,
  StartklarVisare: 22,
};

export const includesRoles = (roles: number[], userRoles?: number[]) => {
  return roles.some((role) => userRoles?.includes(role));
};

export const isDrivingPolicyAdmin = (userRoles: number[], id: number) => {
  return includesRoles([Roles.SuperAdmin], userRoles) || id === 1895; // || // Mattias Frick ID hard coded (because he doesn't need the super-admin role);
  // return id === 1895; // || // Mattias Frick ID hard coded (because he doesn't need the super-admin role);
};

export const isAuthorized = (
  allowedRoles: number[],
  deniedRoles: number[],
  userRoles?: number[]
) => {
  if (!userRoles) {
    return false; // If no user roles are provided, deny access
  }

  if (deniedRoles.length > 0 && includesRoles(deniedRoles, userRoles)) {
    return false;
  }

  if (allowedRoles.length > 0) {
    return includesRoles(allowedRoles, userRoles);
  }

  return true; // If no role controls are specified, allow access
};

export interface AccessRule {
  allow: number[];
  deny: number[];
}

export const DefaultWebAdminPolicy: AccessRule = {
  allow: [Roles.Admin],
  deny: [Roles.Riddermark, Roles.ExternalUser],
};

// Hemankarmappningen under Inställningar. Skriver hemankare på hela fälttestarkåren och
// hör ihop med personalkortet, därför HR utöver superadmin — men inte Admin i stort.
// Backend gatear samma två roller (GlobalSettingsController.PreviewHomeAnchorMapping).
export const HomeAnchorMappingAccessPolicy: AccessRule = {
  allow: [Roles.SuperAdmin, Roles.HR],
  deny: DefaultWebAdminPolicy.deny,
};

// Riktvärdet för andel annonsbilar på lastbil under Inställningar (beslut 2026-09-24). AnnonsAdmin
// ser vyn, men bara SuperAdmin får ändra siffran. Backend gatear samma roller
// (InternalDeliveryTruckShareController): läsning AnnonsAdmin + SuperAdmin, skrivning SuperAdmin.
export const TruckShareTargetViewAccessPolicy: AccessRule = {
  allow: [Roles.AnnonsAdmin, Roles.SuperAdmin],
  deny: DefaultWebAdminPolicy.deny,
};

export const TruckShareTargetEditAccessPolicy: AccessRule = {
  allow: [Roles.SuperAdmin],
  deny: DefaultWebAdminPolicy.deny,
};

// Ruttprovet under Inställningar: kör 17:00-jobbet när som helst. Backend gatear samma roll
// (RoutePlanFeedbackController.TriggerSample).
export const RoutePlanFeedbackTriggerAccessPolicy: AccessRule = {
  allow: [Roles.SuperAdmin],
  deny: DefaultWebAdminPolicy.deny,
};

// Dold felsökningsvy för Google Maps-nycklar (Routes.tests.testMapsApiKey). Ingen länk i menyn.
export const MapsApiKeyTestAccessPolicy: AccessRule = {
  allow: [Roles.SuperAdmin],
  deny: DefaultWebAdminPolicy.deny,
};

// Inställningar → Ruttplanering → Inställningar: ruttplaneringens globala inställningar. Bara
// SuperAdmin får skriva dem (GlobalSettingsController.UpdateSetting), så fliken visas bara för dem.
export const RoutePlanningGlobalSettingsAccessPolicy: AccessRule = {
  allow: [Roles.SuperAdmin],
  deny: DefaultWebAdminPolicy.deny,
};

export const EmployeesPageAccessPolicy: AccessRule = {
  allow: [Roles.Admin, Roles.TillgänglighetsVisare],
  deny: [Roles.Riddermark, Roles.ExternalUser],
};

export const AvailabilityViewAccessPolicy: AccessRule = {
  allow: [Roles.Admin, Roles.TillgänglighetsVisare],
  deny: [Roles.Riddermark, Roles.ExternalUser],
};

export const isAvailabilityReadOnly = (userRoles?: number[]) => {
  if (!userRoles) return false;
  const writeRoles = [Roles.Admin];
  if (includesRoles(writeRoles, userRoles)) return false;
  return includesRoles([Roles.TillgänglighetsVisare], userRoles);
};

export const StartklarViewAccessPolicy: AccessRule = {
  allow: [Roles.Admin, Roles.StartklarVisare],
  deny: [Roles.Riddermark, Roles.ExternalUser],
};

// True för användare som har StartklarVisare men inte Admin — de ska bara nå
// startklar-skärmen. (Admin behåller full åtkomst; speglar isAvailabilityReadOnly.)
export const isStartklarOnly = (userRoles?: number[]) => {
  if (!userRoles) return false;
  const writeRoles = [Roles.Admin];
  if (includesRoles(writeRoles, userRoles)) return false;
  return includesRoles([Roles.StartklarVisare], userRoles);
};

export const InternalDeliveryGroupAccessPolicy: AccessRule = {
  allow: [Roles.Riddermark, Roles.Annons, Roles.Admin, Roles.SuperAdmin],
  deny: [Roles.ExternalUser],
};

export const AccessAssignmentRequestCountEndpointPolicy: AccessRule = {
  allow: [Roles.Admin, Roles.Annons, Roles.AnnonsAdmin],
  deny: [],
};

export const KPIsAccessPolicy: AccessRule = {
  allow: [Roles.SuperAdmin, Roles.Admin, Roles.TeamLead],
  deny: [Roles.Riddermark, Roles.ExternalUser],
};

export const RiddermarkKPIsAccessPolicy: AccessRule = {
  allow: [Roles.RiddermarkLedning, Roles.SuperAdmin],
  deny: [],
};

// Ledningsrapporten — den utskrivbara sidan som mailas till RM:s ledningsgrupp.
// Smalare än RM-nyckeltal med flit: den vyn är till för att gräva i talen, den här för att
// publicera dem, och det är ett fåtal personer som gör det.
export const ManagementReportAccessPolicy: AccessRule = {
  allow: [Roles.SuperAdmin],
  deny: [],
};

// Rapporten "Hastighet & GPS" — samma sida monterad under Kvalitet och
// Efterkontroll (flyttad från HR 2026-09). Rollen räcker inte ensam:
// feature-flaggan HRSpeedingReportEnabled avgör också (se
// useHasAccessToHRSpeedingReport + KPIController).
export const HRSpeedingAccessPolicy: AccessRule = {
  allow: [
    Roles.SuperAdmin,
    Roles.HR,
    Roles.Efterkontroll,
    Roles.KvalitetOUtbildning,
  ],
  deny: [Roles.ExternalUser],
};

// HR > LAS-dagar. Bara HR — inte SuperAdmin eller Kvalitet, som annars delar HR-fliken: det är
// personaluppgifter som underlag för anställningstvister. Backend gatear samma roll
// (HRController.LasDaysRoles).
export const LasDaysAccessPolicy: AccessRule = {
  allow: [Roles.HR],
  deny: DefaultWebAdminPolicy.deny,
};

// HR > Tidsrapport (fast tid, arbetad tid, löneunderlag per fälttestare). Lönedata: bara HR,
// som LAS-dagarna. Backend gatear samma roll (HRController, WorkTimeReport-endpoints).
export const WorkTimeReportAccessPolicy: AccessRule = {
  allow: [Roles.HR],
  deny: DefaultWebAdminPolicy.deny,
};

// Kvalitetsvyn "Godkänt med anmärkning" — delas av route, NavBar och sidan.
export const QualityFlagsAccessPolicy: AccessRule = {
  allow: [Roles.KvalitetOUtbildning, Roles.Efterkontroll, Roles.SuperAdmin],
  deny: DefaultWebAdminPolicy.deny,
};

// Kvalitet (BETA) > SMS-utskick. Snävare än resten av fliken: Efterkontroll kommer åt
// kvalitetsvyerna men ska inte kunna skicka meddelanden till hela fälttestarkåren.
// Backend gatear samma två roller (QualityController.BulkSmsRoles).
export const BulkSmsAccessPolicy: AccessRule = {
  allow: [Roles.KvalitetOUtbildning, Roles.SuperAdmin],
  deny: DefaultWebAdminPolicy.deny,
};

export const getRolesForHandlingCaseType = (caseType: CaseTypeEnum) => {
  if (caseType === CaseTypeEnum.TestDrive) {
    return [Roles.Admin, Roles.ExternalAdmin, Roles.Riddermark];
  }
  return [Roles.Admin, Roles.ExternalAdmin];
};

// Riddermark (även RiddermarkLedning) får inte ändra status på ett uppdrag som Zäkra har godkänt (ärende 639055: ett
// autogodkänt uppdrag backades till Test klart av en RM-användare). Provkörningar hanterar RM
// själva och är undantagna. Speglar RiddermarkAssignmentStatusLock i backend, som är spärren som
// gäller — här styrs bara att statusfältet låses i formuläret.
export const isRiddermarkLockedFromApprovedStatus = (
  userRoles: number[] | undefined,
  savedStatus: AssignmentStatusEnum | undefined,
  caseType: CaseTypeEnum | undefined
) =>
  savedStatus === AssignmentStatusEnum.Approved &&
  caseType !== CaseTypeEnum.TestDrive &&
  includesRoles([Roles.Riddermark, Roles.RiddermarkLedning], userRoles) &&
  !includesRoles([Roles.SuperAdmin], userRoles);

export default Roles;
